<template>
  <div
    class="fixed top-0 left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue overflow-auto"
  >
    <LoadingScreen :loaded="loaded" :message="loadingMessage"></LoadingScreen>

    <ModalConfirmation
      v-if="confirm"
      :text="confirm.text"
      @yes="confirm.yes()"
      @no="confirm.no()"
    ></ModalConfirmation>

    <ErrorBox
      v-if="errorHeading || errorContent"
      :heading="errorHeading"
      :content="errorContent"
      :fixed="errorFixed"
      @closeError="
        errorHeading = '';
        errorContent = '';
      "
    ></ErrorBox>

    <ModalDeleteCheck
      v-if="deleteCheck"
      :text="$t('global.defaults.tenancy', 1)"
      @closeModal="deleteCheck = false"
      @deleteMe="deleteMe()"
    ></ModalDeleteCheck>

    <ModalWrapper v-if="addUsageShow" @closeModal="addUsageShow = false">
      <form class="pt-2 mb-4 relative" @submit.prevent="addUsage">
        <div
          class="absolute -top-6 -right-6 text-xl p-2 hover:text-nucleus-primaryBlue transition leading-none cursor-pointer icon-close rounded-full"
          :title="$t('titles.close')"
          @click="addUsageShow = false"
        ></div>
        <div class="text-lg pb-4 font-semibold">
          {{ $t("pages.tenancy.addCredits") }}
        </div>
        <Input
          id="usage-add"
          v-model:value="creditsToAdd"
          :update:value="validateCredits(creditsToAdd)"
          input-type="number"
          :label="$t('inputs.credits.label')"
          min="0"
          :max-length="inputMaxLengths.credits"
          :placeholder="$t('inputs.credits.placeholder')"
          required
        />
        <Button
          type="submit"
          :name="$t('buttons.addCredits')"
          :disabled="!creditsToAdd || !validateCredits(creditsToAdd) || loading"
          :loading="loading"
        ></Button>
      </form>
    </ModalWrapper>

    <div v-if="loaded">
      <HeaderMini
        :name="tenancy.name"
        :to="{ name: 'tenancies' }"
        :can-delete="true"
        :can-edit="true"
        :can-deactivate="true"
        :can-add-usage="true"
        :state="tenancy.state"
        :no-menu="false"
        :text="$t('global.defaults.tenancy', 1)"
        @deleteCheck="deleteCheck = true"
        @edit="editTenancy()"
        @deactivate="confirmDeactivateTenancy()"
        @activate="activateTenancy()"
        @addUsage="addUsageShow = true"
      ></HeaderMini>
      <HeaderShadow></HeaderShadow>

      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <div>
            {{ $t("global.defaults.status") }}
            <span class="font-bold text-nucleus-primaryBlue">{{
              mapState(tenancy.state)
            }}</span>
          </div>
          <!-- <div class="bg-nucleus-white p-2 text-nucleus-primaryBlue">
            This is a note about the tenancy
          </div> -->
        </div>

        <div class="bg-nucleus-white">
          <div class="flex items-center px-4 py-6 border-b">
            <div
              class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-tenancy"
            ></div>
            <div class="text-2xl">
              {{ tenancy.name }}
            </div>
          </div>
          <div class="px-4 pt-6">
            <InfoItem
              :heading="$t('pages.tenancy.tenancyStatus')"
              :value="mapState(tenancy.state)"
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.created')"
              :value="new Date(Date.parse(tenancy.createdOn)).toString()"
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.internalId')"
              :value="tenancy.tenantId"
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.Region')"
              :value="
                regions.find((v) => v.id.toString() === tenancy.region)?.name ||
                regions[0].name
              "
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.language')"
              :value="
                languages.find((v) => v.id.toString() === tenancy.language)
                  ?.name || languages[0].name
              "
            ></InfoItem>

            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.allowance") }}
              </div>
            </div>
            <InfoItem
              :heading="$t('pages.tenancy.currentLicense')"
              :value="tenancy.license"
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.expiryDate')"
              :value="getDate(tenancy.licenseExpiry)"
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.hoursUsed')"
              :value="getHoursUsed()"
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.balance')"
              :value="getHoursLeft()"
            ></InfoItem>
            <InfoItem
              :heading="$t('inputs.allowanceWarningThreshold.label')"
              :value="
                tenancy.allowanceWarningThreshold ||
                defaults.allowanceWarningThreshold
              "
            ></InfoItem>

            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.contactDetails") }}
              </div>
            </div>
            <InfoItem
              :heading="$t('inputs.name.label')"
              :value="tenancy.contact.name"
            ></InfoItem>
            <InfoItem
              :heading="$t('inputs.email.label')"
              :value="tenancy.contact.email"
            ></InfoItem>
            <InfoItem
              :heading="$t('inputs.phone.label')"
              :value="tenancy.contact.phone"
            ></InfoItem>
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.sessionSettings") }}
              </div>
            </div>
            <InfoItem
              :heading="$t('inputs.maxBroadcasts.label')"
              :value="tenancy.maxBroadcasts || defaults.maxBroadcasts"
            ></InfoItem>
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.serverConnection") }}
              </div>
            </div>
            <div class="pb-6">
              <div class="text-sm text-nucleus-text">
                {{ $t("pages.tenancy.connectionStatus") }}
              </div>
              <div v-if="connectionStatus.status" class="mt-1 text-black">
                {{ connectionStatus.status }}
              </div>
              <div
                v-if="connectionStatus.error"
                class="mt-1 text-nucleus-error"
              >
                {{ connectionStatus.error }}
              </div>
              <TextLinkBasic
                class="mt-6"
                :name="$t('pages.tenancy.checkConnection')"
                @click="testConnection()"
              ></TextLinkBasic>
            </div>
            <InfoItem
              :heading="$t('pages.tenancy.apiVersion')"
              :value="
                apiVersionOptions.find(
                  (v) => v.id.toString() === tenancy.api.version
                )?.name || apiVersionOptions[0].name
              "
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.apiUsername')"
              :value="tenancy.api.username"
            ></InfoItem>
            <InfoItem
              :heading="$t('pages.tenancy.vpnUrl')"
              :value="tenancy.vpnServer"
            ></InfoItem>
            <div class="pb-6">
              <div class="text-sm text-nucleus-text">
                {{ $t("pages.tenancy.vpnCertificate") }}
              </div>
              <TextLinkBasic
                v-if="!downloadPending"
                class="mt-6"
                :name="$t('pages.tenancy.download')"
                @click="downloadVpnCert()"
              ></TextLinkBasic>
              <div v-if="downloadPending" class="text-sm text-nucleus-text">
                {{ $t("pages.tenancy.generating") }}
              </div>
            </div>
            <div class="pb-6">
              <div class="text-sm text-nucleus-text">
                {{ $t("pages.tenancy.mediaIp") }}
              </div>
              <div v-if="tenancy.elasticIps">
                <div
                  v-for="allocation in tenancy.elasticIps"
                  :key="allocation.publicIp"
                  class="mt-1 text-black"
                >
                  {{ allocation.publicIp }}
                </div>
              </div>
              <TextLinkBasic
                v-if="!tenancy.elasticIps"
                class="mt-6"
                :name="$t('pages.tenancy.reserve')"
                @click="confirmReserveIps()"
              ></TextLinkBasic>
              <TextLinkBasic
                v-if="tenancy.elasticIps"
                class="mt-6"
                :name="$t('pages.tenancy.release')"
                @click="confirmReleaseIps()"
              ></TextLinkBasic>
            </div>
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.userAccess") }}
              </div>
            </div>
            <InfoItemList
              :heading="$t('pages.tenancy.ipWhitelist')"
              :list="
                tenancy.ipWhitelist ? tenancy.ipWhitelist : ['No restriction']
              "
            ></InfoItemList>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderMini from "@/components/global/HeaderMini.vue"; // @ is an alias to /src
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import InfoItem from "@/components/utils/InfoItem.vue"; // @ is an alias to /src
import InfoItemList from "@/components/utils/InfoItemList.vue"; // @ is an alias to /src
import LoadingScreen from "@/components/global/LoadingScreen.vue";
import ModalConfirmation from "@/components/global/ModalConfirmation.vue";
import ModalDeleteCheck from "@/components/global/ModalDeleteCheck.vue";
import ModalWrapper from "@/components/global/ModalWrapper.vue";
import TextLinkBasic from "@/components/utils/TextLinkBasic.vue"; // @ is an alias to /src
import ErrorBox from "@/components/utils/ErrorBox.vue";
import Input from "@/components/utils/Input.vue";
import Button from "@/components/utils/Button.vue";
import ApiVersionOptionsJson from "@/store/data/apiVersionOptions.json";
import RegionsJson from "@/store/data/regions.json";
import LanguagesJson from "@/store/data/languages.json";

import { getPrettyDateTime } from "@/utils/prettyDate";
import { mapAwsUserStatus } from "@/utils/awsMappings";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    HeaderMini,
    HeaderShadow,
    InfoItem,
    InfoItemList,
    LoadingScreen,
    ModalConfirmation,
    ModalDeleteCheck,
    ModalWrapper,
    TextLinkBasic,
    ErrorBox,
    Input,
    Button,
  },
  data() {
    return {
      defaults: {
        maxBroadcasts: 4,
        allowanceWarningThreshold: 100,
      },
      tenancy: {},
      connectionStatus: {},
      loaded: false,
      loading: false,
      deleteCheck: false,
      deactivateCheck: false,
      confirm: undefined,
      loadingMessage: this.$t("global.defaults.loadingTenancy"),
      downloadPending: false,
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      apiVersionOptions: ApiVersionOptionsJson,
      regions: RegionsJson,
      languages: LanguagesJson,
      addUsageShow: false,
      creditsToAdd: 0,
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  beforeMount() {
    this.getTenancy();
  },
  mounted() {
    document.title =
      this.$t("global.defaults.tenancy", 1) +
      " - " +
      this.$t("global.defaults.browserTitle");
  },
  methods: {
    mapState(state: string) {
      return this.$t(mapAwsUserStatus(state));
    },
    getTenancy() {
      this.$store
        .dispatch("getTenancy", this.$route.params.tenancyId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.loaded = true;
            this.tenancy = response.data;
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    deleteMe() {
      this.loaded = false;
      this.loadingMessage = this.$t("global.defaults.deletingTenancy");
      this.$store
        .dispatch("deleteTenancy", this.$route.params.tenancyId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.loaded = true;
            console.log(response.data);
            this.$store.dispatch("changeUpdate", "tenancy");
            this.$router.push({ name: "tenancies" });
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    confirmDeactivateTenancy() {
      this.confirm = {
        text: this.$t("pages.tenancy.deactivateMessage"),
        yes: () => {
          this.deactivateTenancy();
          this.confirm = undefined;
        },
        no: () => (this.confirm = undefined),
      };
    },
    deactivateTenancy() {
      this.loaded = false;
      this.loadingMessage = this.$t("pages.tenancy.deactivating");
      this.$store
        .dispatch("disableTenancy", this.$route.params.tenancyId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.loaded = true;
            console.log(response.data);
            this.getTenancy();
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    activateTenancy() {
      this.loaded = false;
      this.loadingMessage = this.$t("pages.tenancy.activating");
      this.$store
        .dispatch("enableTenancy", this.$route.params.tenancyId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.loaded = true;
            console.log(response.data);
            this.getTenancy();
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loaded = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    editTenancy() {
      this.$router.push({
        name: "editTenancy",
        params: { tenancyId: this.$route.params.tenancyId },
      });
    },
    testConnection() {
      this.connectionStatus = {};
      this.$store
        .dispatch("getTenancyConnectionStatus", this.$route.params.tenancyId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.connectionStatus = response.data;
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    downloadVpnCert() {
      this.downloadPending = true;
      this.$store
        .dispatch("generateClientCertificate", this.$route.params.tenancyId)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response.data && response.data.url) {
            console.log(response.data);
            window.open(response.data.url, "_blank");
          }
          this.downloadPending = false;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.downloadPending = false;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    confirmReserveIps() {
      this.confirm = {
        text: this.$t("pages.tenancy.reserveIpMessage"),
        yes: () => {
          this.reserveIps();
          this.confirm = undefined;
        },
        no: () => (this.confirm = undefined),
      };
    },
    reserveIps() {
      this.$store
        .dispatch("reserveMediaServerIps", this.$route.params.tenancyId)
        .then((response: Record<string, unknown>) => {
          console.log(response);
          if (response) {
            this.getTenancy();
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    confirmReleaseIps() {
      this.confirm = {
        text: this.$t("pages.tenancy.releaseIpMessage"),
        yes: () => {
          this.releaseIps();
          this.confirm = undefined;
        },
        no: () => (this.confirm = undefined),
      };
    },
    releaseIps() {
      this.$store
        .dispatch("releaseMediaServerIps", this.$route.params.tenancyId)
        .then((response: Record<string, unknown>) => {
          console.log(response);
          if (response) {
            this.getTenancy();
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    getHoursLeft() {
      let remaining =
        this.tenancy.creditsTotal -
        this.tenancy.creditsUsed -
        this.tenancy.creditsExpired;
      let remainingText;
      if (remaining === 0) {
        remainingText = this.$t("pages.usage.0Hours");
      } else if (remaining > -1 && remaining < 0) {
        remainingText = this.$t("pages.usage.0HoursDebt");
      } else if (remaining < 0) {
        remainingText = this.$t("pages.usage.0HoursDebtAmount", {
          remaining: Math.floor(Math.abs(remaining)),
        });
      } else if (remaining > 0 && remaining < 1) {
        remainingText = this.$t("pages.usage.lessThan1Remaining");
      } else {
        remainingText = this.$t("pages.usage.remaining", {
          remaining: Math.floor(remaining),
        });
      }
      return remainingText;
    },
    getHoursUsed() {
      let usedText;
      if (this.tenancy.creditsUsed > 0 && this.tenancy.creditsUsed < 1) {
        usedText = this.$t("pages.usage.lessThan1Used", {
          remaining: this.tenancy.creditsTotal,
        });
      } else {
        usedText = this.$t("pages.usage.used", {
          used: Math.floor(this.tenancy.creditsUsed),
          total: this.tenancy.creditsTotal,
        });
      }
      return usedText;
    },
    getDate(date: Date) {
      return getPrettyDateTime(date);
    },
    validateCredits(credits: number) {
      if (credits > 0) {
        return true;
      } else {
        return false;
      }
    },
    addUsage() {
      this.$store
        .dispatch("addUsageCredits", {
          tenancyId: this.$route.params.tenancyId,
          credits: this.creditsToAdd,
        })
        .then((response: Record<string, unknown>) => {
          console.log(response);
          if (response) {
            this.getTenancy();
          }
          this.addUsageShow = false;
          this.creditsToAdd = 0;
          this.loading = false;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
          this.addUsageShow = false;
          this.creditsToAdd = 0;
          this.loading = false;
        });
      console.log(this.usage);
      this.loading = true;
    },
  },
})
export default class TenancyDetail extends Vue {}
</script>
